import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import type { RootState } from 'app/store';
import { formatDate } from 'utils';
import { PRIVACY_GLOBAL_PLAN_CODE } from 'utils/constants';
import { BRAND_CODES } from 'brand-config';

const selectOfferState = (state: RootState) => state.offer;

export const selectOfferCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.code,
);

export const selectOfferPlanCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.planCode,
);

export const selectCurrentPlanFamily = createSelector(
  [selectOfferState],
  (offerState) => Boolean(offerState.planFamily),
);

export const selectOfferPlanTier = createSelector(
  [selectOfferState],
  (offerState) =>
    offerState.planTier?.trim() ? offerState.planTier.trim() : '',
);

export const selectOfferPlanFeatures = createSelector(
  [selectOfferState],
  (offerState) =>
    Array.isArray(offerState.planFeatures) ? offerState.planFeatures : [],
);

export const selectOfferCustomerBilled = createSelector(
  [selectOfferState],
  (offerState) => offerState.customerBilled,
);

export const selectOfferBillingType = createSelector(
  [selectOfferState],
  (offerState) => offerState.billingType,
);

export const selectOfferPlanCardPriceCents = createSelector(
  [selectOfferState],
  (offerState) => {
    const cp = offerState.customerPrice;
    if (cp != null && !Number.isNaN(Number(cp))) return cp;
    return offerState.price ?? 0;
  },
);

export const selectOfferSubscriptionTermName = createSelector(
  [selectOfferState],
  (offerState) => {
    const tn = offerState.subscriptionPlan?.termName?.trim();
    return tn ? tn.toUpperCase() : 'MONTHLY';
  },
);

export const selectOfferPlanName = createSelector(
  [selectOfferState],
  (offerState) => offerState.planName ?? '',
);

export const selectOfferBrandCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.brandCode,
);

export const selectOfferTermEndDate = createSelector(
  [selectOfferState],
  (offerState) => {
    if (offerState.serviceEndDate) {
      return formatDate(offerState.serviceEndDate, 'MM/DD/YYYY');
    }
    if (offerState.servicePeriod) {
      return dayjs().add(offerState.servicePeriod, 'M').format('MM/DD/YYYY');
    }
    return 'N/A';
  },
);

export const selectOfferPrice = createSelector(
  [selectOfferState],
  (offerState) => {
    return offerState.price;
  },
);

export const selectOfferTerm = createSelector(
  [selectOfferState],
  (offerState) => {
    return offerState.subscriptionPlan.termDescription;
  },
);

export const selectIsPrivacyGlobal = createSelector(
  [selectOfferBrandCode, selectOfferPlanCode],
  (brandCode, planCode) =>
    brandCode === BRAND_CODES.privacyShield &&
    planCode === PRIVACY_GLOBAL_PLAN_CODE,
);

export const selectOfferGroupCode = createSelector(
  [selectOfferState],
  (offerState) => offerState.companyCode,
);

export const selectCompanyType = createSelector(
  [selectOfferState],
  (offerState) => offerState.group.type.toLowerCase(),
);

export const selectIsOfferDetailsExist = createSelector(
  [selectOfferState],
  (offerState) => !!offerState.code,
);

export const selectScheduledOffer = createSelector(
  [selectOfferState],
  (offerState) => offerState.scheduledOffer,
);
